/**
 * /ressources-pedagogiques — Rubrique publique
 * Accessible à tous : visiteurs, élèves, moniteurs.
 * Présente les livrets & infographies CODE 2 ROUES : lecture, téléchargement, partage QR.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Download, ExternalLink, Eye, Maximize2, X, QrCode, BookOpen,
  Sparkles, ImageIcon, FileText, Bike, ChevronRight, Route,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const RESSOURCES = [
  {
    id: 'livret-am-topolino-8h-2026',
    type: 'livret',
    badge: 'NOUVEAU · LIVRET AM 2026',
    badgeBg: 'bg-red-600',
    title: 'Livret élève — AM Topolino Mission 8H',
    subtitle: 'Comprendre · Expérimenter · Verbaliser · Corriger · Transférer',
    desc: 'Le livret officiel Code 2 Roues 2026 pour la formation AM (voiturette Topolino) : 16 pages chartrées avec couverture premium, sommaire des 5 séquences officielles, 4 missions pédagogiques détaillées, mini-QCM à chaque mission, fiches de révision et auto-évaluation finale. Téléchargement direct — accessible même sans compte.',
    tags: ['AM · Voiturette', '8h de formation', '16 pages', 'Édition 2026', 'Accessible visiteurs'],
    pdf: `${process.env.REACT_APP_BACKEND_URL || ''}/api/livrets/am-8h-2026/pdf`,
    pdfName: 'Livret-AM-Topolino-Mission-8H-Code2Roues-2026.pdf',
    interactive: '/livrets/voiturette-8h',
    cover: null,
    gradient: 'from-red-600/30 via-rose-500/15 to-transparent',
    accent: '#cf2031',
    icon: BookOpen,
  },
  {
    id: 'livret-plateau-a2',
    type: 'livret',
    badge: 'LIVRET PREMIUM',
    badgeBg: 'bg-red-600',
    title: 'Livret élève — Plateau A1/A2',
    subtitle: 'Prendre en main · Maîtriser · Progresser',
    desc: '7 chapitres pédagogiques du référentiel plateau : bienvenue, moto & commandes, parcours, les 6 manœuvres, progression, prêt pour l\'examen.',
    tags: ['Plateau', '7 chapitres', 'A1/A2', 'Version premium'],
    pdf: '/livret-plateau/print',
    pdfName: 'Livret-Plateau-A2-CODE2ROUES.pdf',
    interactive: '/livret-plateau',
    cover: '/livrets/cover-livret-cles-plateau-a2.png',
    gradient: 'from-red-600/30 via-orange-500/15 to-transparent',
    accent: '#ef4444',
    icon: BookOpen,
  },
  {
    id: 'livret-plateau-flipbook',
    type: 'livret',
    badge: 'FLIPBOOK NUMÉRIQUE',
    badgeBg: 'bg-red-700',
    title: 'Livret Plateau — Version flipbook',
    subtitle: 'Feuilleter comme un vrai livret papier',
    desc: 'Version numérique interactive style « flipbook » : chaque page défile comme un livret imprimé. Idéal sur tablette ou grand écran. Contenu identique au livret interactif mais présenté en pleines pages illustrées.',
    tags: ['Plateau', 'Flipbook', 'Feuilletable', 'Version numérique'],
    pdf: null,
    pdfName: null,
    interactive: '/livrets/livret-plateau-numerique-interactif.html',
    external: true,
    cover: null,
    gradient: 'from-red-700/30 via-orange-600/15 to-transparent',
    accent: '#b91c1c',
    icon: BookOpen,
  },
  {
    id: 'livret-circulation-a2',
    type: 'livret',
    badge: 'NOUVEAU · LIVRET',
    badgeBg: 'bg-cyan-600',
    title: 'Livret élève — Circulation A1/A2',
    subtitle: 'Sortir · Observer · Anticiper',
    desc: '7 chapitres pour l\'épreuve circulation (~40 min sur route, coefficient 3) : prise en main, regard/allure/position, trajectoire de sécurité, cohabitation avec les autres usagers, vérifications sécurité, prêt pour l\'examen.',
    tags: ['Circulation', '7 chapitres', 'A1/A2', 'Nouveau'],
    pdf: '/livret-circulation/print',
    pdfName: 'Livret-Circulation-A2-CODE2ROUES.pdf',
    interactive: '/livret-circulation',
    cover: null,
    gradient: 'from-cyan-600/30 via-blue-500/15 to-transparent',
    accent: '#06b6d4',
    icon: Route,
  },
  {
    id: 'cours-premiere-circulation-a2',
    type: 'cours',
    badge: 'COURS PREMIUM',
    badgeBg: 'bg-red-600',
    title: 'Cours — Ma première circulation A2',
    subtitle: 'Regarder · Comprendre · Anticiper · Décider · Agir',
    desc: '14 chapitres illustrés pour la 1ʳᵉ leçon sur route : structure de la séance, évaluation statique, groupe & radio, incidents, méthode d\'intersection et de giratoire, bilan pédagogique et les 7 réflexes CODE 2 ROUES. Consultable en mode lecture ou en mode présentation plein écran (idéal pour projection en salle).',
    tags: ['Circulation A2', '14 chapitres', '2h de séance', 'Présentation plein écran'],
    pdf: null,
    pdfName: null,
    interactive: '/cours/premiere-circulation-a2',
    cover: '/images/articles/cours-c1.png',
    gradient: 'from-red-600/30 via-orange-500/15 to-transparent',
    accent: '#cf2031',
    icon: Sparkles,
  },
  {
    id: 'cours-deuxieme-circulation-a2',
    type: 'cours',
    badge: 'NOUVEAU · COURS 2',
    badgeBg: 'bg-red-600',
    title: 'Cours — Ma 2ᵉ circulation A2',
    subtitle: 'Observer · Anticiper · Adapter',
    desc: '14 chapitres illustrés pour la 2ᵉ leçon : reprise du dernier bilan, autonomie retrouvée, regard qui prend de l\'avance, recherche des indices, espace de sécurité, intersections & giratoires en autonomie, lecture des véhicules, leader pédagogique, guidage progressif de l\'enseignant, bilan n°2.',
    tags: ['Circulation A2', '14 chapitres', 'Séance 2', 'Autonomie'],
    pdf: null,
    pdfName: null,
    interactive: '/cours/deuxieme-circulation-a2',
    cover: '/images/articles/cours2-c1.png',
    gradient: 'from-red-600/30 via-orange-500/15 to-transparent',
    accent: '#cf2031',
    icon: Sparkles,
  },
  {
    id: 'cours-troisieme-circulation-a2',
    type: 'cours',
    badge: 'NOUVEAU · COURS 3',
    badgeBg: 'bg-red-600',
    title: 'Cours — Ma 3ᵉ circulation A2',
    subtitle: 'Décider · Planifier · S\'adapter · Devenir autonome',
    desc: '14 chapitres pour la 3ᵉ séance : regard qui va chercher loin devant, hiérarchisation des risques, gestion de plusieurs informations, choix de positionnement, masques de visibilité, changements de voie, dépassement, virages autonomes, prise de décision et justification. « Je sais pourquoi je le fais. »',
    tags: ['Circulation A2', '14 chapitres', 'Séance 3', 'Vers l\'autonomie'],
    pdf: null,
    pdfName: null,
    interactive: '/cours/troisieme-circulation-a2',
    cover: null,
    gradient: 'from-red-600/30 via-orange-500/15 to-transparent',
    accent: '#cf2031',
    icon: Sparkles,
  },
  {
    id: 'cours-quatrieme-circulation-a2',
    type: 'cours',
    badge: 'NOUVEAU · COURS 4',
    badgeBg: 'bg-red-600',
    title: 'Cours — Ma 4ᵉ circulation A2',
    subtitle: 'Conduire · Anticiper · Choisir · Sécuriser · Être autonome',
    desc: '14 chapitres pour la 4ᵉ séance : point de départ du dernier bilan, prise de responsabilité, autonomie continue, suivi d\'une direction, gestion des transitions d\'environnement, planification, imprévu, savoir renoncer, partage de la route, courtoisie préventive, régularité dans la durée, regard d\'examinateur, auto-analyse et bilan n°4. « Prendre seul une décision sûre. »',
    tags: ['Circulation A2', '14 chapitres', 'Séance 4', 'Niveau examen'],
    pdf: null,
    pdfName: null,
    interactive: '/cours/quatrieme-circulation-a2',
    cover: null,
    gradient: 'from-red-600/30 via-orange-500/15 to-transparent',
    accent: '#cf2031',
    icon: Sparkles,
  },
  {
    id: 'cours-cinquieme-circulation-a2',
    type: 'cours',
    badge: 'NOUVEAU · COURS 5',
    badgeBg: 'bg-red-600',
    title: 'Cours — Ma 5ᵉ circulation A2',
    subtitle: 'Consolider · S\'adapter · Rester régulier · Se rapprocher du niveau examen',
    desc: '14 chapitres pour la 5ᵉ séance : bilan-contrat, autonomie immédiate au départ, radio discrète, itinéraire autonome, détection des changements de contexte, construction des marges, situations complexes, décision assumée, prévisibilité, voie rapide, récupération après une erreur, séquence type examen, auto-débriefing et bilan n°5. « Bien conduire, seul et de façon régulière. »',
    tags: ['Circulation A2', '14 chapitres', 'Séance 5', 'Proche examen'],
    pdf: null,
    pdfName: null,
    interactive: '/cours/cinquieme-circulation-a2',
    cover: null,
    gradient: 'from-red-600/30 via-orange-500/15 to-transparent',
    accent: '#cf2031',
    icon: Sparkles,
  },
  {
    id: 'cours-sixieme-circulation-a2',
    type: 'cours',
    badge: 'FINAL · COURS 6',
    badgeBg: 'bg-red-600',
    title: 'Cours — Ma 6ᵉ circulation A2',
    subtitle: 'Valider · Consolider · Être autonome · Se préparer à l\'examen',
    desc: '14 chapitres pour la 6ᵉ et dernière séance de circulation (12 h minimales atteintes) : mobilisation complète des compétences, environnements variés, lecture avancée de la route, marges maintenues, décisions, imprévu, phase d\'autonomie, régularité dans la durée, séquence proche des conditions d\'évaluation, auto-bilan, bilan final à 3 niveaux (autonome / à consolider / à travailler) et deux chemins possibles avant l\'examen.',
    tags: ['Circulation A2', '14 chapitres', 'Séance 6', 'Bilan final', '12 h minimales'],
    pdf: null,
    pdfName: null,
    interactive: '/cours/sixieme-circulation-a2',
    cover: null,
    gradient: 'from-red-600/30 via-orange-500/15 to-transparent',
    accent: '#cf2031',
    icon: Sparkles,
  },
  {
    id: 'cours-premiere-plateau-a2',
    type: 'cours',
    badge: 'NOUVEAU · PLATEAU 1',
    badgeBg: 'bg-red-600',
    title: 'Cours plateau — Ma 1ʳᵉ séance A2',
    subtitle: 'Tu découvres · Tu ressens · Tu contrôles ta moto',
    desc: '14 chapitres pour la première séance plateau A2 : contact avec la moto, installation, déplacement sans moteur, démarrage/arrêt, point de patinage, coordination accélérateur/embrayage/frein arrière, regard directeur, allure lente stable, changement de direction, premier demi-tour, changements de rapports, premier freinage maîtrisé et bilan à 3 niveaux (maîtrise / acquisition / besoin d\'aide).',
    tags: ['Plateau A2', '14 chapitres', 'Séance 1', 'Découverte moto', 'Hors circulation'],
    pdf: null, pdfName: null,
    interactive: '/cours/premiere-plateau-a2',
    cover: null,
    gradient: 'from-red-600/30 via-orange-500/15 to-transparent',
    accent: '#cf2031',
    icon: Sparkles,
  },
  {
    id: 'cours-deuxieme-plateau-a2',
    type: 'cours',
    badge: 'NOUVEAU · PLATEAU 2',
    badgeBg: 'bg-red-600',
    title: 'Cours plateau — Ma 2ᵉ séance A2',
    subtitle: 'Tu stabilises · Tu coordonnes · Tu diriges · Tu gagnes en précision',
    desc: '14 chapitres pour la 2ᵉ séance plateau : réactivation des acquis, allure lente stable, coordination des commandes, regard anticipateur, direction basse allure, demi-tour précis, portes et enchaînements, arrêts précis, transitions d\'allure, premier freinage progressé.',
    tags: ['Plateau A2', '14 chapitres', 'Séance 2', 'Coordination', 'Précision'],
    pdf: null, pdfName: null,
    interactive: '/cours/deuxieme-plateau-a2',
    cover: null,
    gradient: 'from-red-600/30 via-orange-500/15 to-transparent',
    accent: '#cf2031',
    icon: Sparkles,
  },
  {
    id: 'cours-troisieme-plateau-a2',
    type: 'cours',
    badge: 'NOUVEAU · PLATEAU 3',
    badgeBg: 'bg-red-600',
    title: 'Cours plateau — Ma 3ᵉ séance A2',
    subtitle: 'Tu accélères · Tu freines · Tu évites · Tu enchaînes',
    desc: '14 chapitres pour la 3ᵉ séance plateau : passage du lent au dynamique, accélération organisée, rapports fluides, freinage préparé et progressif, précision d\'arrêt, évitement (regard, contre-braquage, retour en trajectoire) et premiers enchaînements complets.',
    tags: ['Plateau A2', '14 chapitres', 'Séance 3', 'Dynamique', 'Évitement'],
    pdf: null, pdfName: null,
    interactive: '/cours/troisieme-plateau-a2',
    cover: null,
    gradient: 'from-red-600/30 via-orange-500/15 to-transparent',
    accent: '#cf2031',
    icon: Sparkles,
  },
  {
    id: 'cours-quatrieme-plateau-a2',
    type: 'cours',
    badge: 'FINAL · PLATEAU 4',
    badgeBg: 'bg-red-600',
    title: 'Cours plateau — Ma 4ᵉ séance A2',
    subtitle: 'Tu consolides · Tu enchaînes · Tu t\'auto-corriges · Tu deviens autonome',
    desc: '14 chapitres pour la 4ᵉ et dernière séance plateau (8 h minimales atteintes) : prise en charge autonome, régularité, passager, transitions sans rupture, freinage et évitement reproductibles, enchaînement complet, conditions proches examen, auto-correction et bilan final à 3 niveaux (autonome / consolider / travailler).',
    tags: ['Plateau A2', '14 chapitres', 'Séance 4', 'Bilan final', '8 h minimales'],
    pdf: null, pdfName: null,
    interactive: '/cours/quatrieme-plateau-a2',
    cover: null,
    gradient: 'from-red-600/30 via-orange-500/15 to-transparent',
    accent: '#cf2031',
    icon: Sparkles,
  },
  {
    id: 'infographie-petit-gabarit',
    type: 'infographie',
    badge: 'INFOGRAPHIE',
    badgeBg: 'bg-orange-500',
    title: '1ᵉʳ cours plateau — Petit gabarit (1,60 m)',
    subtitle: 'S\'arrêter et poser les pieds au sol',
    desc: 'Fiche visuelle A3 : objectif du jour, principe clé, référence de position à l\'arrêt (CFMoto 450NK), progression en 4 étapes, à retenir/à éviter.',
    tags: ['Petit gabarit', '1er cours', 'CFMoto 450NK', 'Ton contrôle · Ta sécurité'],
    pdf: '/livrets/infographie-1er-cours-petit-gabarit.png',
    pdfName: 'Infographie-1er-Cours-Petit-Gabarit.png',
    interactive: null,
    cover: '/livrets/infographie-1er-cours-petit-gabarit.png',
    gradient: 'from-orange-600/30 via-red-500/15 to-transparent',
    accent: '#f97316',
    icon: ImageIcon,
  },
];

export default function RessourcesPedagogiques() {
  const [preview, setPreview] = useState(null); // ressource en lecture
  const [shareQR, setShareQR] = useState(null); // ressource à partager

  useEffect(() => {
    document.title = 'Ressources pédagogiques · code2roues.fr';
  }, []);

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://code2roues.fr';

  return (
    <div className="min-h-screen bg-neutral-950 text-white pb-24">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600/25 via-orange-500/10 to-transparent" />
        <div className="absolute -right-20 -bottom-16 opacity-[0.06] pointer-events-none">
          <Bike size={340} strokeWidth={1} />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 py-10 sm:py-14">
          <Link to="/" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[12px] font-semibold mb-5" data-testid="btn-home">
            <ArrowLeft size={14} /> Retour au site
          </Link>
          <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.28em] text-red-400 mb-3">
            <Sparkles size={11} /> Rubrique pédagogique
          </div>
          <h1 className="text-[32px] sm:text-[46px] font-black text-white leading-[1.05]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Ressources <span className="text-red-400">pédagogiques</span>
          </h1>
          <p className="text-white/70 text-[15px] mt-3 max-w-2xl leading-relaxed">
            Nos livrets et fiches visuelles pour <strong className="text-white">accompagner ta formation moto</strong>. Consulte en ligne, télécharge en PDF ou partage à un(e) camarade via QR code. Accessible aux <strong>élèves</strong>, <strong>enseignants</strong> et <strong>visiteurs</strong>.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-5 text-[11px] text-white/60">
            <span className="inline-flex items-center gap-1 bg-white/[0.06] border border-white/10 px-2.5 py-1 rounded-full">
              <BookOpen size={11} /> Version livre interactive
            </span>
            <span className="inline-flex items-center gap-1 bg-white/[0.06] border border-white/10 px-2.5 py-1 rounded-full">
              <Download size={11} /> PDF téléchargeable
            </span>
            <span className="inline-flex items-center gap-1 bg-white/[0.06] border border-white/10 px-2.5 py-1 rounded-full">
              <QrCode size={11} /> Partage QR
            </span>
          </div>
        </div>
      </div>

      {/* Grille des ressources */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {RESSOURCES.map((r) => (
            <RessourceCard
              key={r.id}
              r={r}
              onPreview={() => setPreview(r)}
              onShare={() => setShareQR({ ...r, url: r.interactive ? `${origin}${r.interactive}` : `${origin}${r.pdf}` })}
            />
          ))}
        </div>

        {/* Bloc CTA vers le livre interactif */}
        <div className="mt-10 rounded-3xl border border-red-500/30 bg-gradient-to-br from-red-600/20 via-orange-500/10 to-transparent p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-red-500/25 border border-red-500/40 flex items-center justify-center shrink-0">
              <BookOpen size={26} className="text-red-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 mb-1">Nouveau · Recommandé</div>
              <h2 className="text-[20px] sm:text-[24px] font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Livre interactif Plateau A2
              </h2>
              <p className="text-white/70 text-[13px] leading-relaxed mt-1">
                Feuillette les 7 chapitres, coche ta progression, remplis ton parcours. Ta progression est sauvegardée sur ton téléphone.
              </p>
            </div>
            <Link
              to="/livret-plateau"
              className="inline-flex items-center gap-1.5 px-5 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white text-[13px] font-black shadow-lg transition shrink-0"
              data-testid="cta-livret-interactif"
            >
              Ouvrir le livre <ChevronRight size={15} />
            </Link>
          </div>
        </div>
      </div>

      {/* Modale de prévisualisation (image ou PDF) */}
      {preview && (
        <PreviewModal ressource={preview} onClose={() => setPreview(null)} />
      )}

      {/* Modale QR partage */}
      {shareQR && (
        <ShareQRModal ressource={shareQR} onClose={() => setShareQR(null)} />
      )}
    </div>
  );
}

function RessourceCard({ r, onPreview, onShare }) {
  const Icon = r.icon;
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br ${r.gradient} p-5 transition hover:border-white/25`} data-testid="ressource-card">
      <div className="absolute top-3 right-3">
        <span className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest ${r.badgeBg} text-white px-2 py-1 rounded-full`}>
          {r.badge}
        </span>
      </div>

      {/* Aperçu visuel */}
      {r.cover ? (
        <button
          onClick={onPreview}
          className="block w-full aspect-video rounded-xl overflow-hidden bg-white/[0.04] border border-white/10 mb-4 group relative"
          data-testid="btn-preview-cover"
        >
          <img src={r.cover} alt={r.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-2 bg-white/95 text-neutral-900 rounded-lg text-[12px] font-black">
              <Maximize2 size={13} /> Agrandir
            </div>
          </div>
        </button>
      ) : (
        <div className="aspect-video rounded-xl bg-white/[0.03] border border-white/10 mb-4 flex flex-col items-center justify-center p-5">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-2" style={{ background: `${r.accent}25`, color: r.accent }}>
            <Icon size={28} />
          </div>
          <div className="text-[11px] font-black uppercase tracking-widest text-white/50">{r.type === 'livret' ? '7 chapitres · A5' : 'Fiche visuelle'}</div>
        </div>
      )}

      <div className="text-[10px] font-black uppercase tracking-[0.22em]" style={{ color: r.accent }}>{r.subtitle}</div>
      <div className="text-white text-[17px] font-black leading-tight mt-1 mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        {r.title}
      </div>
      <p className="text-white/60 text-[12.5px] leading-snug mb-3">{r.desc}</p>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {r.tags.map((t, i) => (
          <span key={i} className="text-[9.5px] font-bold uppercase tracking-widest bg-white/[0.06] border border-white/10 text-white/60 px-2 py-0.5 rounded-full">
            {t}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        {r.interactive ? (
          r.external ? (
            <a
              href={r.interactive}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1 px-2 py-2.5 rounded-lg text-white text-[11px] font-black shadow-md transition"
              style={{ background: r.accent }}
              data-testid="btn-open-interactive-external"
            >
              <BookOpen size={12} /> Ouvrir
            </a>
          ) : (
            <Link
              to={r.interactive}
              className="inline-flex items-center justify-center gap-1 px-2 py-2.5 rounded-lg text-white text-[11px] font-black shadow-md transition"
              style={{ background: r.accent }}
              data-testid="btn-open-interactive"
            >
              <BookOpen size={12} /> Lire
            </Link>
          )
        ) : (
          <button
            onClick={onPreview}
            className="inline-flex items-center justify-center gap-1 px-2 py-2.5 rounded-lg text-white text-[11px] font-black shadow-md transition"
            style={{ background: r.accent }}
            data-testid="btn-open-preview"
          >
            <Eye size={12} /> Voir
          </button>
        )}
        <a
          href={r.pdf}
          download={r.pdfName}
          className="inline-flex items-center justify-center gap-1 px-2 py-2.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-white text-[11px] font-bold transition"
          data-testid="btn-download"
        >
          <Download size={12} /> PDF
        </a>
        <button
          onClick={onShare}
          className="inline-flex items-center justify-center gap-1 px-2 py-2.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-white text-[11px] font-bold transition"
          data-testid="btn-share"
        >
          <QrCode size={12} /> QR
        </button>
      </div>
    </div>
  );
}

function PreviewModal({ ressource, onClose }) {
  const isImage = ressource.cover && ressource.cover.endsWith('.png');
  const isPdf = ressource.pdf && ressource.pdf.endsWith('.pdf');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur p-4" onClick={onClose} data-testid="modal-preview">
      <div className="relative max-w-6xl max-h-[92vh] w-full flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between bg-neutral-900 px-4 py-2.5 rounded-t-xl border border-white/10">
          <div className="text-white text-[13px] font-bold truncate">{ressource.title}</div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={ressource.pdf}
              download={ressource.pdfName}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[11px] font-black transition"
              data-testid="btn-preview-download"
            >
              <Download size={11} /> Télécharger
            </a>
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-white/[0.10] text-white/70 hover:text-white"
              data-testid="btn-preview-close"
              aria-label="Fermer"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 bg-neutral-950 rounded-b-xl border-x border-b border-white/10 overflow-auto">
          {isImage ? (
            <img src={ressource.cover} alt={ressource.title} className="w-full h-auto" />
          ) : isPdf ? (
            <iframe src={ressource.pdf} title={ressource.title} className="w-full" style={{ height: '80vh' }} />
          ) : (
            <div className="p-10 text-center text-white/50">Aperçu indisponible</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ShareQRModal({ ressource, onClose }) {
  const [copied, setCopied] = useState(false);
  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(ressource.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* silent */ }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="modal-share">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className={`${ressource.badgeBg} text-white px-5 py-3 flex items-center justify-between`}>
          <div>
            <div className="text-[9px] font-black uppercase tracking-[0.22em] text-white/80">Partage rapide</div>
            <div className="text-[14px] font-bold truncate">{ressource.title}</div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1" data-testid="btn-share-close" aria-label="Fermer">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 flex flex-col items-center">
          <div className="bg-white p-3 rounded-lg border-2 border-neutral-200">
            <QRCodeSVG value={ressource.url} size={220} level="H" bgColor="#ffffff" fgColor="#0a0a0b" />
          </div>
          <div className="mt-4 text-center">
            <div className="text-[11px] text-neutral-500 uppercase tracking-widest font-bold mb-1">Scannez avec un smartphone</div>
            <div className="text-[13px] text-neutral-800">Accès direct à la ressource en 1 clic</div>
          </div>
          <div className="mt-4 w-full flex items-center gap-2 border border-neutral-300 rounded-lg px-3 py-2 bg-neutral-50">
            <div className="flex-1 text-[11px] text-neutral-700 truncate font-mono" data-testid="share-url">{ressource.url}</div>
            <button
              onClick={copyUrl}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-black transition ${
                copied ? 'bg-emerald-600 text-white' : 'bg-neutral-900 text-white hover:bg-neutral-700'
              }`}
              data-testid="btn-copy-share-url"
            >
              {copied ? 'Copié ✓' : 'Copier'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
